import { AppMessagesProvider } from '@common/context/appMessagesContext';
import { Auth0ProviderWithHistory } from '@common/context/auth0Context';
import { DevicesProvider } from '@common/context/DevicesContext';
import { MovementProvider } from '@common/context/MovementContext';
import { UserProvider } from '@common/context/userContext';
import { WarningsProvider } from '@common/context/warningsContext';
import Chatbot from '@components/Chatbot';
import ErrorBoundary from '@components/ErrorBoundary';
import GTMUserTracker from '@components/GTMUserTracker';
import LaunchDarklyUserTracker from '@components/LaunchDarklyUserTracker';
import { AdminProtectedRoute, ProtectedRoute } from '@components/ProtectedRoute';
import DataPrefetcher from '@containers/Prefetch/DataPrefetcher';
import { StateProvider } from '@state';
import { lazyWithRetry } from '@utils/lazy-with-retry';
import React, { Suspense, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter as Router, Redirect, Route, Switch, useHistory } from 'react-router-dom';

const AdminPage = lazyWithRetry(() => import('@containers/Admin'));
const Callback = lazyWithRetry(() => import('@containers/Callback'));
const Consignments = lazyWithRetry(() => import('@containers/Consignments'));
const ConsignmentAdd = lazyWithRetry(() => import('@containers/Consignments/ConsignmentAdd'));
const ConsignmentAddSelect = lazyWithRetry(() => import('@containers/Consignments/ConsignmentAddSelect'));
const ConsignmentCopy = lazyWithRetry(() => import('@containers/Consignments/ConsignmentCopy'));
const ConsignmentCopySelectTemplate = lazyWithRetry(
    () => import('@containers/Consignments/ConsignmentCopySelectTemplate')
);
const ConsignmentEdit = lazyWithRetry(() => import('@containers/Consignments/ConsignmentEdit'));
const ConsignmentSumary = lazyWithRetry(() => import('@containers/Consignments/ConsignmentSummary'));
const DevicesMovement = lazyWithRetry(() => import('@containers/Devices/Index'));
const Edecs = lazyWithRetry(() => import('@containers/Edecs'));
const Logout = lazyWithRetry(() => import('@containers/Logout'));
const Profile = lazyWithRetry(() => import('@containers/Profile'));
const Register = lazyWithRetry(() => import('@containers/Register'));
const Templates = lazyWithRetry(() => import('@containers/Templates'));
const TemplateAdd = lazyWithRetry(() => import('@containers/Templates/TemplateAdd'));
const TemplateEdit = lazyWithRetry(() => import('@containers/Templates/TemplateEdit'));
const Welcome = lazyWithRetry(() => import('@containers/Welcome'));

const ONE_HOUR = 60 * 60 * 1000;
const ONE_DAY = 24 * ONE_HOUR;

// Create React Query client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: ONE_DAY, // Data stays fresh for 24 hours
            cacheTime: ONE_DAY, // Cache is kept for 24 hours
            retry: 3,
            refetchOnWindowFocus: false,
        },
    },
});

// TODO: Workaround for react-router-dom@5.3.4 + history@4.9 + React 18 incompatibility.
// `history.goBack()` is unreliable here: under React 18 the popstate handler can
// end up detached from `window` even while react-router believes it's subscribed,
// so goBack updates the URL but never propagates through react-router. The in-app
// "Previous Page" button therefore uses `history.push(getPreviousPath())` instead.
// This component listens on every PUSH/REPLACE and records the prior pathname so
// `getPreviousPath()` can return a meaningful value. Remove once we upgrade to
// react-router-dom@6 (history@5, React-18-native).
let previousPath: string | null = null;
let currentPath: string = typeof window !== 'undefined' ? window.location.pathname : '';

export const getPreviousPath = (): string | null => previousPath;

const PreviousPathTracker: React.FC = () => {
    const history = useHistory();
    useEffect(() => {
        const unlisten = history.listen((loc, action) => {
            if (action === 'PUSH' || action === 'REPLACE') {
                previousPath = currentPath;
                currentPath = loc.pathname;
            }
        });
        return unlisten;
    }, [history]);
    return null;
};

const AppRouter: React.FC<{}> = () => {
    return (
        <>
            <QueryClientProvider client={queryClient}>
                {/* Last resort for errors outside a ProtectedRoute page (Welcome, Callback, Logout, providers).
                    Pages have their own boundary in ProtectedRoute so the Header stays usable. */}
                <ErrorBoundary>
                    <Suspense fallback={null}>
                        <WarningsProvider>
                            <Router>
                                <PreviousPathTracker />
                                <Auth0ProviderWithHistory>
                                    <GTMUserTracker />
                                    {/* Swaps LaunchDarkly's anonymous context for one carrying the
                                        signed-in email, so per-user flag targeting can match. */}
                                    <LaunchDarklyUserTracker />
                                    <UserProvider>
                                        {/* Inside UserProvider so it can send the signed-in
                                        customer's identity (token, PICs) to the bot. */}
                                        <Chatbot />
                                        <DataPrefetcher>
                                            {/* ENVDB-7836: the App_Messages banner renders once, in ProtectedRoute; pages
                                            holding a consignment publish their forms and status through this provider. */}
                                            <AppMessagesProvider>
                                                <Switch>
                                                    <Route
                                                        path="/"
                                                        exact
                                                    >
                                                        <Welcome />
                                                    </Route>
                                                    <Route
                                                        path="/callback"
                                                        exact
                                                    >
                                                        <Callback />
                                                    </Route>
                                                    <StateProvider>
                                                        <MovementProvider>
                                                            <DevicesProvider>
                                                                <Switch>
                                                                    <Route
                                                                        path="/logout"
                                                                        exact
                                                                    >
                                                                        <Logout />
                                                                    </Route>

                                                                    <ProtectedRoute
                                                                        path="/consignments/edit/:id/:section"
                                                                        component={ConsignmentEdit}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/consignments/:id/movement"
                                                                        component={DevicesMovement}
                                                                    />
                                                                    <ProtectedRoute
                                                                        path="/consignments/summary/:id"
                                                                        component={ConsignmentSumary}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/consignments/add-select"
                                                                        exact
                                                                        component={ConsignmentAddSelect}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/consignments/select-template"
                                                                        exact
                                                                        component={ConsignmentCopySelectTemplate}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/consignments/copy/:id"
                                                                        exact
                                                                        component={ConsignmentCopy}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/profile"
                                                                        exact
                                                                        component={Profile}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/consignments/add"
                                                                        exact
                                                                        component={ConsignmentAdd}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/consignments"
                                                                        component={Consignments}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/templates/edit/:id/:section"
                                                                        component={TemplateEdit}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/templates/add/:id"
                                                                        exact
                                                                        component={TemplateAdd}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/templates"
                                                                        exact
                                                                        component={Templates}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/edecs"
                                                                        exact
                                                                        component={Edecs}
                                                                    />

                                                                    <ProtectedRoute
                                                                        path="/register"
                                                                        exact
                                                                        component={Register}
                                                                    />

                                                                    <AdminProtectedRoute
                                                                        path="/admin"
                                                                        exact
                                                                        component={AdminPage}
                                                                    />

                                                                    <Route path="/*">
                                                                        <Redirect to="/" />
                                                                    </Route>
                                                                </Switch>
                                                            </DevicesProvider>
                                                        </MovementProvider>
                                                    </StateProvider>
                                                </Switch>
                                            </AppMessagesProvider>
                                        </DataPrefetcher>
                                    </UserProvider>
                                </Auth0ProviderWithHistory>
                            </Router>
                        </WarningsProvider>
                    </Suspense>
                </ErrorBoundary>
            </QueryClientProvider>
            <style
                jsx
                global
            >
                {`
                    @use 'sass:color';
                    @use 'vars' as *;
                    @use 'utils' as *;
                    @use 'mixins' as *;

                    .container {
                        margin-top: 50px;
                    }

                    body {
                        background: $color-grey96;
                        -webkit-overflow-scrolling: touch;

                        @media (prefers-color-scheme: dark) {
                            background: color.adjust($color-grey96, $lightness: -85%);
                        }

                        &.sb-show-main {
                            margin-top: 0;
                        }
                    }
                `}
            </style>
        </>
    );
};

export default AppRouter;
